import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Pencil, Trash2, Save, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export const CategoryManager = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [newName, setNewName] = useState('');

  const fetchCategories = async () => {
    try {
      const data = await getCategories();
      setCategories(data);
    } catch (error) {
      toast.error('Errore nel caricamento delle categorie');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await createCategory({ name: newName });
      toast.success('Categoria aggiunta');
      setNewName('');
      fetchCategories();
    } catch (error) {
      toast.error('Errore creazione categoria');
    }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      await updateCategory(id, { name: editName });
      toast.success('Categoria aggiornata');
      setEditingId(null);
      fetchCategories();
    } catch (error) {
      toast.error('Errore aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro? Se cancelli questa categoria, le attività collegate potrebbero rimanere senza categoria.')) return;
    try {
      await deleteCategory(id);
      toast.success('Categoria eliminata');
      fetchCategories();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Torna alla Dashboard
          </Button>
          <h1 className="text-2xl font-bold text-gray-800">Gestione Categorie</h1>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Form Aggiunta */}
          <Card>
            <CardHeader>
              <CardTitle>Nuova Categoria</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="flex gap-2">
                <Input 
                  value={newName} 
                  onChange={(e) => setNewName(e.target.value)} 
                  placeholder="Es. Tecnologia" 
                />
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Lista Categorie */}
          <Card className="md:col-span-2">
            <CardContent className="pt-6">
              {loading ? (
                <p>Caricamento...</p>
              ) : (
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <div key={cat.id} className="flex items-center justify-between p-3 bg-white border rounded-lg shadow-sm">
                      {editingId === cat.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input 
                            value={editName} 
                            onChange={(e) => setEditName(e.target.value)} 
                            className="h-9"
                          />
                          <Button size="sm" onClick={() => handleUpdate(cat.id)} className="bg-green-600">
                            <Save className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <span className="font-medium text-gray-700">{cat.name}</span>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => {
                                setEditingId(cat.id);
                                setEditName(cat.name);
                              }}
                            >
                              <Pencil className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDelete(cat.id)}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};